import type { EnergyHistory } from "@spf/contracts";
import { Card } from "../Card.js";
import { Failed, Loading } from "../ViewState.js";
import { cn } from "../../lib/cn.js";
import { coverageNote } from "../../lib/energy.js";
import type { Async } from "../../lib/useAsync.js";
import type { EnergyResult } from "../../lib/useEnergy.js";

/**
 * Energy delivered today, as the design's headline figure and progress pill.
 *
 * The design measures the day against a target. There is no target setting on
 * this instance yet, so the bar shows the day so far with the remainder left
 * striped, and says which is which. When a target arrives the bar fills
 * against it and nothing else here changes.
 *
 * The figure is integrated on the server from stored readings over the site's
 * own day. Until slice 5 it was the inverter's `eac_dischar_today` counter,
 * which turned out to count only load served from the grid: once a PV array
 * was connected it read 0.0 on a day the house used 6.8 kWh
 * (docs/DECISIONS/0027).
 */
export function TotalEnergyWidget({
  today: { state: today, retry },
  onExpand,
  className,
}: {
  today: EnergyResult;
  onExpand?: () => void;
  className?: string;
}) {
  const delivered = today.kind === "ready" ? today.value.totals.loadKwh : null;

  return (
    <Card
      onExpand={onExpand}
      title="Energy Summary"
      headerRight={
        <span className="flex items-center gap-1.5 rounded-full border border-accent-2/20 bg-accent-2/10 px-2.5 py-1 text-[11px] font-medium text-accent-2-ink">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-2" />
          <span>Receiving</span>
        </span>
      }
      className={cn("flex flex-col justify-between", className)}
    >
      <Delivered today={today} onRetry={retry} />

      <div className="mt-7">
        <div className="mb-2.5 flex items-center justify-between text-xs font-medium sm:text-sm">
          <span className="text-ink-muted">Today</span>
          <span className="tabular font-semibold text-ink-muted">No target set</span>
        </div>

        <div className="flex h-6 w-full items-center rounded-full border border-line bg-line/40 p-1 sm:h-7">
          <div
            aria-hidden="true"
            className="h-full rounded-full bg-accent transition-all duration-700 ease-out"
            // Without a target there is no percentage to draw, so the filled
            // part is a fixed sliver rather than a made-up proportion.
            style={{ width: delivered === null || delivered === 0 ? "0%" : "12%" }}
          />
          <div
            aria-hidden="true"
            className="diagonal-stripes ml-1.5 h-full flex-1 rounded-full opacity-60"
          />
        </div>
      </div>
    </Card>
  );
}

function Delivered({ today, onRetry }: { today: Async<EnergyHistory>; onRetry: () => void }) {
  if (today.kind === "loading") return <Loading label="Loading today’s energy…" />;
  if (today.kind === "error") return <Failed message={today.message} onRetry={onRetry} />;

  const { totals } = today.value;
  const note = coverageNote(totals);
  if (totals.loadKwh === null) {
    return (
      <div>
        <p className="tabular text-3xl font-bold tracking-tight text-ink sm:text-4xl">—</p>
        <p className="mt-1 text-xs text-ink-muted">Nothing has been recorded today yet.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="tabular flex items-baseline gap-1">
        <span className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
          {totals.loadKwh.toLocaleString("en-GB", { maximumFractionDigits: 1 })}
        </span>
        <span className="text-xs font-normal text-ink-muted sm:text-sm">kWh</span>
      </p>
      <p className="mt-1 text-xs text-ink-muted">
        Delivered to the house today, worked out from the readings.
        {note !== null && ` ${note}.`}
      </p>
    </div>
  );
}
